import { useCallback, useMemo, useRef } from 'react';
import { useQuery } from '@tanstack/react-query';
import apiClient from '../../API/apiClient';
import endpoints from '../../API/endpoints';
import { VideoNotificationDto } from './constants';
import { activeVideoNotificationsQueryKey } from './sync/constants';
import { useVideoNotificationRecall } from './sync/useVideoNotificationRecall';
import { reconcileWatchedFlags } from './sync/watchedVideoIdsStore';
import { readQueueSnapshot, writeQueueSnapshot } from './sync/queueSnapshotStore';

/**
 * Agent-facing video-notification API — shared by B2C and Backoffice.
 * The operator CRUD endpoints live in the Backoffice module
 * (`modules/video-notification/VideoNotificationApi.tsx`).
 */

/** How long a fetched queue is considered fresh by react-query itself. */
const QUEUE_STALE_TIME_MS = 5 * 60 * 1000;

/**
 * GET /ms/video-notifications/active
 * Returns the pending queue for the logged-in agent. An empty array means
 * there is nothing to show. Anything the agent has already confirmed is
 * filtered out server-side, so every item here starts unseen.
 */
export const fetchActiveVideoNotifications = async (): Promise<
  VideoNotificationDto[]
> => {
  const res = await apiClient.get(endpoints.VIDEO_NOTIFICATIONS.ACTIVE, {
    // This runs on a timer in the background. A failure is worth a console
    // warning and a retry, not a "502 - Bad Gateway" toast landing on an agent
    // mid-booking. The rejection still propagates.
    suppressErrorToast: true,
  });
  const videos = res?.data?.data?.videos;
  return Array.isArray(videos) ? videos : [];
};

export interface VideoNotificationHistoryPage {
  videos: VideoNotificationDto[];
  has_more: boolean;
  next_cursor: { before: string; before_id: number } | null;
}

/**
 * GET /ms/video-notifications/history - updates older than the 30-day feed, newest first, seen and unseen.
 * Pass the previous page's next_cursor for the next one.
 */
export const fetchVideoNotificationHistory = async (
  cursor?: VideoNotificationHistoryPage['next_cursor']
): Promise<VideoNotificationHistoryPage> => {
  const res = await apiClient.get(endpoints.VIDEO_NOTIFICATIONS.HISTORY, {
    params: cursor ? { before: cursor.before, before_id: cursor.before_id } : undefined,
  });
  const data = res?.data?.data;
  return {
    videos: Array.isArray(data?.videos) ? data.videos : [],
    has_more: !!data?.has_more,
    next_cursor: data?.next_cursor ?? null,
  };
};

/** POST /ms/video-notifications/:id/watched */
export const markVideoNotificationWatchedApi = (id: number | string) =>
  // Deliberately *not* toast-suppressed: unlike the queue fetch, this is a
  // direct response to the user pressing "Done", and failing silently there
  // would leave them thinking the video was confirmed when it was not.
  apiClient.post(`${endpoints.VIDEO_NOTIFICATIONS.MARK_WATCHED}/${id}/watched`);

interface UseActiveVideoNotificationsOptions {
  /**
   * Host permission gate — the request is not fired at all when false, so
   * users who cannot receive notifications never hit the endpoint.
   */
  enabled?: boolean;
  /**
   * Stable identity of the logged-in person (`agent:42` / `user:7`). It
   * namespaces the cache, the stored watched ids and the cross-tab channel, so
   * two people sharing a browser never see each other's queue. Cross-tab sync
   * is simply off while this is null.
   */
  scope?: string | null;
}

export const useActiveVideoNotificationsApi = ({
  enabled = true,
  scope = null,
}: UseActiveVideoNotificationsOptions = {}) => {
  // `publishQueue` comes from the recall hook, which in turn needs this query's
  // `refetch` — the refs break that cycle without re-creating either on every
  // render.
  const publishQueueRef = useRef<(videos: VideoNotificationDto[]) => void>(
    () => undefined
  );
  const refetchRef = useRef<() => Promise<any>>(async () => ({}));

  const queryKey = useMemo(() => activeVideoNotificationsQueryKey(scope), [scope]);

  // The queue another tab already fetched - rendered at once, no request (see queueSnapshotStore)
  const snapshot = useMemo(() => (enabled && scope ? readQueueSnapshot(scope) : null), [enabled, scope]);

  const { data, isLoading, isFetching, refetch } = useQuery(
    queryKey,
    async () => {
      const videos = await fetchActiveVideoNotifications();
      if (scope) writeQueueSnapshot(scope, videos);
      return scope ? reconcileWatchedFlags(scope, videos) : videos;
    },
    {
      // Never fetches by itself - not even on mount. Every request goes through
      // `useVideoNotificationRecall` (cross-tab lease + shared 5 minute gap),
      // which also runs once when the tab opens: a fresh snapshot means no call.
      // Without a scope there is nothing to coordinate through - fetch alone.
      enabled: enabled && !scope,
      initialData: snapshot && scope ? reconcileWatchedFlags(scope, snapshot.videos) : undefined,
      initialDataUpdatedAt: snapshot?.at,
      // Every refetch trigger is pinned explicitly rather than inherited: B2C
      // creates a bare QueryClient (react-query's own defaults) while
      // Backoffice sets a 24h staleTime with every refetch flag off, and this
      // queue must behave identically in both. Focus, reconnect and interval
      // are owned by `useVideoNotificationRecall`, which gates them on a
      // cross-tab lease; react-query's versions would bypass that and let five
      // tabs fire at once.
      refetchOnWindowFocus: false,
      refetchOnMount: false,
      refetchOnReconnect: false,
      refetchInterval: false,
      retry: false,
      staleTime: QUEUE_STALE_TIME_MS,
      onSuccess: (videos: VideoNotificationDto[]) =>
        publishQueueRef.current(videos),
    }
  );

  refetchRef.current = refetch;
  const stableRefetch = useCallback(() => refetchRef.current(), []);

  const { markWatched, publishQueue } = useVideoNotificationRecall({
    scope,
    enabled,
    refetch: stableRefetch,
  });

  publishQueueRef.current = publishQueue;

  return {
    notifications: data ?? [],
    isLoading,
    isFetching,
    refetch,
    /**
     * Confirms a video for every tab of this app: remembers it across reloads,
     * flags it in the cache so the blocking overlay lifts, and broadcasts it.
     */
    markWatched,
  };
};
